import React from "react";
import "./App.css";
import Container from "./Container";
import Footer from "./Footer";
import NavBar from "./NavBar";
import SegmentedLCDPage from "./pages/Display/SegmentedLCDPage";
import HdmiDisplayPage from "./pages/Display/HdmiDisplayPage"; // LANDING_PAGES_WIRED
import HdmiRaspberryPiGuidePage from "./pages/Display/HdmiRaspberryPiGuidePage";
import HdmiJetsonWindowsGuidePage from "./pages/Display/HdmiJetsonWindowsGuidePage";
import UartDisplayPage from "./pages/Display/UartDisplayPage";

import ContactPage from "./pages/ContactPage";
import EMSPage from "./pages/EMSPage";
import EmbeddedDesignPage from "./pages/EmbeddedDesignPage";
import AboutPage from "./pages/AboutPage";
import ReturnPolicyPage from "./pages/ReturnPolicyPage";
import ShippingPolicyPage from "./pages/ShippingPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import CookieConsent from "./components/CookieConsent";
import ProductListing from "./pages/ProductListingPage";
import SevenInchesPage from "./pages/SmartHMI/SevenInchesPage";

import DsyncStudioFeatures from "./pages/SmartHMI/DsyncStudioFeatures";
import BlogDetailPage from "./pages/BlogDetailPage";
import LearnHubPage from "./pages/Learn/LearnHubPage";
import LearnArticlePage from "./pages/Learn/LearnArticlePage";

import TFTGridDisplay from "./pages/Display/TFTGridDisplay";
import ProductPage from "./pages/ProductPage";
import CanonicalTag from "./CanonicalTag";
import RouteSeo from "./components/RouteSeo";
import NotFoundPage from "./pages/NotFoundPage";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useSearchParams,
  Navigate,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CartProvider } from "./context/CartContext";
import { CompareProvider } from "./context/CompareContext";
import CompareBar from "./components/CompareBar";
import TFTDetailPage from "./pages/Display/TFTDetailPage"
import ChatWidget from "./components/ChatWidget/ChatWidget";
import { openChat } from "./components/ChatWidget/openChat";
import Breadcrumb from "./Breadcrumb/Breadcrumb";
import CareerPage from "./pages/Career/CareerPage";
// ChatBot (Cherry) moved to Tixaru -- not mounted on the public website.
// Import left commented rather than deleted so it's a one-line change if
// that ever changes. Component itself untouched.
// import ChatBot from "./components/ChatBot";


// Pages that are not prerendered (cart, checkout, account, orders, tickets, login,
// search, compare, courier admin) load on demand, so their code (date picker, phone
// number library, forms) stays out of the main bundle every visitor downloads.
// Prerendered marketing pages stay in the main bundle on purpose: the app replaces
// their static HTML on load, and a lazy chunk would flash that content away.
const AdminAssignCourier = React.lazy(() => import("./pages/AdminOrders/AdminAssignCourier"));
const LoginPage = React.lazy(() => import("./pages/Auth/LoginPage"));
const AccountPage = React.lazy(() => import("./pages/Auth/AccountPage"));
const CartPage = React.lazy(() => import("./pages/CartPage/CartPage"));
const CheckoutPage = React.lazy(() => import("./pages/CartPage/Checkoutpage"));
const MyOrders = React.lazy(() => import("./pages/OrderPage/OrderPage"));
const TicketsPage = React.lazy(() => import("./pages/TicketsPage/TicketsPage"));
const OrderDetailsPage = React.lazy(() => import("./pages/OrderPage/OrderDetailPage"));
const SearchResultsPage = React.lazy(() => import("./pages/SearchResultsPage"));
const ComparePage = React.lazy(() => import("./pages/ComparePage"));
function Lazy({ children }) {
  return <React.Suspense fallback={null}>{children}</React.Suspense>;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    // A hash (e.g. /contact#rfq) targets a specific section on the page --
    // scroll to that element instead of forcing the page back to the top.
    // The route's enter animation (AnimatePresence in AnimatedRoutes) takes
    // a moment to lay out, so wait a tick before measuring/scrolling.
    if (hash) {
      const id = hash.slice(1);
      const timer = setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return () => clearTimeout(timer);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname, hash]);

  return null;
}



function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>

        <Route
          path="/"
          element={
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              <Container />
            </motion.div>
          }
        />

        {/* ✅ CHANGE 2: /about now renders the single unified AboutPage directly */}
        <Route
          path="/about"
          element={
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.5 }}
            >
              <AboutPage />
            </motion.div>
          }
        />
        <Route
          path="/about/return-policy"
          element={
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.5 }}
            >
              <ReturnPolicyPage />
            </motion.div>
          }
        />
        <Route
          path="/about/shipping-policy"
          element={
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.5 }}
            >
              <ShippingPolicyPage />
            </motion.div>
          }
        />

        <Route path="/about/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/about/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/about/terms" element={<TermsPage />} />

        {/* The footer's Cancellation Policy link: cancellations are covered on the Return Policy page. */}
        <Route path="/about/cancellation-policy" element={<Navigate to="/about/return-policy#cancellations" replace />} />

        {/* Redirect any other old /about/* sub-routes to the new unified /about page */}
        <Route path="/about/*" element={<Navigate to="/about" replace />} />

<Route path="/admin-assign-courier" element={<Lazy><AdminAssignCourier /></Lazy>} />

        <Route
          path="/tft-display"
          element={
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              <TFTGridDisplay />
            </motion.div>
          }
        />


        {/* Static, prerendered landing pages for the two product lines that
            Google Ads sends traffic to. The Firestore category grids stay as
            the full variant lists; these pages carry the marketing copy,
            Product/FAQ schema and the keywords people actually search. */}
        <Route
          path="/hdmi-display"
          element={
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <HdmiDisplayPage />
            </motion.div>
          }
        />
        <Route
          path="/hdmi-display/raspberry-pi-setup"
          element={
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <HdmiRaspberryPiGuidePage />
            </motion.div>
          }
        />
        <Route
          path="/hdmi-display/jetson-windows-ubuntu-setup"
          element={
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <HdmiJetsonWindowsGuidePage />
            </motion.div>
          }
        />
        <Route
          path="/uart-display"
          element={
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <UartDisplayPage />
            </motion.div>
          }
        />

        <Route
          path="/segmented-lcd"
          element={
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <SegmentedLCDPage />
            </motion.div>
          }
        />

        {/* Segmented LCDs are 100% custom-built -- there is no off-the-shelf
            SKU to browse, so this exact category/sub-category URL used to
            render the same custom marketing page as /segmented-lcd instead
            of the generic Firestore product grid (ProductListing below).
            That left two URLs serving identical content (same H1, same
            copy), which the 2026-09-13 SEO audit flagged as a duplicate --
            nginx now 301s this path to /segmented-lcd for any direct/crawler
            hit (see nginx.conf), and this client-side redirect keeps in-app
            navigation (e.g. a category card built from a Firestore slug)
            converging on the same canonical URL instead of rendering the
            duplicate page again. */}
        <Route
          path="/category/passive-displays/segmented-display"
          element={<Navigate to="/segmented-lcd" replace />}
        />

        {/* Segmented LCD knowledge base -- clickable spec-strip pills on
            the page above link here. /learn/:slug must come after any
            more specific /learn/* route (none exist yet) so it can serve
            as the catch-all for every article slug in
            src/data/learnArticles.js. Opacity-only transition, same
            reasoning as the dynamic category route below: avoids a
            transform ancestor breaking position:fixed descendants. */}
        <Route
          path="/learn"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              <LearnHubPage />
            </motion.div>
          }
        />
        <Route
          path="/learn/:slug"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              <LearnArticlePage />
            </motion.div>
          }
        />


        {/* Overview page retired -- Dsync-Studio.jsx removed, all its
            content now lives on /dsync-studio-features. Redirect so old
            links, bookmarks and search results still land somewhere real. */}
        <Route
          path="/dsync-studio"
          element={<Navigate to="/dsync-studio-features" replace />}
        />

        <Route
          path="/dsync-studio-features"
          element={
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.6 }}
            >
              <DsyncStudioFeatures />
            </motion.div>
          }
        />

        {/* Old standalone product page, superseded by the live Smart UART
            Display category, which holds the current real products. */}
        <Route
          path="/Smart-UART-Display"
          element={<Navigate to="/uart-display" replace />}
        />

        {/* These two used to render a hardcoded static-JSON product page
            (SevenInchesPage's own default pageData), bypassing Tixaru
            entirely. That static entry's part number (B-1098-VUN-0500-BX)
            no longer exists in the live catalog, so there's no single real
            product left to redirect to 1:1 -- send visitors to the real,
            live Smart UART Display category instead, which today holds
            exactly the current real equivalent (B-1102-VUI-0700-BX). */}
        <Route
          path="/seven-inches"
          element={<Navigate to="/uart-display" replace />}
        />
        <Route
          path="/7-inch-smart-uart"
          element={<Navigate to="/uart-display" replace />}
        />

        
 <Route
          path="/contact"
          element={
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <ContactPage />
            </motion.div>
          }
        />


        <Route
          path="/ems"
          element={
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <EMSPage />
            </motion.div>
          }
        />

        <Route path="/login" element={<Lazy><LoginPage /></Lazy>} />

        <Route path="/account" element={<Lazy><AccountPage /></Lazy>} />

        {/* Fixed: was /tft/:categorySlug/:subCategorySlug/:productSlug/:partNoSlug
            (4 segments). The real Firestore schema has no product-line
            grouping layer between subCategories and productData, so the
            productSlug segment has nothing to resolve against — removed. */}
        <Route path="/tft/:categorySlug/:subCategorySlug/:partNoSlug" element={<TFTDetailPage />} />
        <Route path="/blog/:slug" element={<BlogDetailPage />} />



        <Route path="/cart" element={<Lazy><CartPage /></Lazy>} />

        <Route path="/checkout" element={<Lazy><CheckoutPage /></Lazy>} />

        <Route
          path="/my-orders"
          element={
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.6 }}
            >
              <Lazy><MyOrders /></Lazy>
            </motion.div>
          }
        />

        <Route path="/my-chat" element={<OpenChatRoute />} />

        <Route
          path="/my-tickets"
          element={
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.6 }}
            >
              <Lazy><TicketsPage /></Lazy>
            </motion.div>
          }
        />

        {/* Same TicketsPage component as /my-tickets above -- it reads the
            :id param to open straight into that ticket's conversation.
            Kept as a shareable/bookmarkable entry point; selecting a
            different ticket afterwards only updates local state, not this
            URL (see TicketsPage.jsx's top comment for why). */}
        <Route
          path="/my-tickets/:id"
          element={
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.6 }}
            >
              <Lazy><TicketsPage /></Lazy>
            </motion.div>
          }
        />

        <Route
          path="/careers"
          element={
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.5 }}
            >
              <CareerPage />
            </motion.div>
          }
        />


        <Route
          path="/order/:id"
          element={<Lazy><OrderDetailsPage /></Lazy>}
        />
        <Route
          path="/embedded"
          element={
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <EmbeddedDesignPage />
            </motion.div>
          }
        />


        {[
          "signage-15",
          "signage-10",
          "signage-7",
          "smart-dongle",
          "rk3566",
          "rk3562",
          "aw133",
        ].map((path) => (
          <Route
            key={path}
            path={`/${path}`}
            element={<Navigate to="https://dristify.com" replace />}
          />
        ))}

        <Route
          path="/category/:categorySlug/:subCategorySlug"
          element={
            // opacity-only -- NOT the usual y-slide transition. Framer
            // Motion applies "y" as a CSS `transform`, and that transform
            // stays on this element (even at rest, even after the
            // animation settles) which makes it the CSS containing block
            // for every `position:fixed` descendant inside it. This page
            // has three: the mobile filter drawer's open button, its
            // backdrop, and the drawer itself -- with a transform ancestor
            // they stopped positioning against the real viewport and
            // instead measured themselves against this wrapper div, which
            // is wider/taller than the screen. Net effect on a phone: the
            // filter button and drawer render off in space, unreachable
            // ("filters doesn't work"). Plain opacity never becomes a
            // `transform`, so it never creates that containing block.
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <ProductListing />
            </motion.div>
          }
        />

        <Route
          path="/search"
          element={
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <Lazy><SearchResultsPage /></Lazy>
            </motion.div>
          }
        />

        <Route
          path="/compare"
          element={
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <Lazy><ComparePage /></Lazy>
            </motion.div>
          }
        />

        <Route
          path="/product/:slug"
          element={
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <ProductPage />
            </motion.div>
          }
        />


        {/* Catch-all. Without this, an unmatched URL rendered the navbar and
            footer with an empty middle -- a soft 404 to Google and a dead end
            for visitors. */}
        <Route path="*" element={<NotFoundPage />} />

      </Routes>
    </AnimatePresence>
  );
}

// BIBA's reply emails link to /my-chat: open the chat bot and show the home page.
function OpenChatRoute() {
  const [params] = useSearchParams();
  const orderId = params.get("order") || "";
  React.useEffect(() => {
    openChat({ orderId });
  }, [orderId]);
  return <Navigate to="/" replace />;
}

// Layout Component — Handles Navbar, Footer, and ScrollToTop
function Layout() {
  const location = useLocation();
  const hideFooter = location.pathname.startsWith("/about");
  const hideGlobalBreadcrumb =
    location.pathname === "/about" ||
    location.pathname === "/about/return-policy" ||
    location.pathname === "/about/shipping-policy" ||
    location.pathname === "/about/cookie-policy" ||
    location.pathname === "/about/privacy-policy" ||
    location.pathname === "/about/terms" ||
    location.pathname === "/embedded" ||
    location.pathname === "/contact" ||
    location.pathname === "/account" ||
    location.pathname.startsWith("/learn");

  return (
    <>
      <ScrollToTop />
      <NavBar />
      {!hideGlobalBreadcrumb && <Breadcrumb />}
      <AnimatedRoutes />
      {!hideFooter && <Footer />}
      <CompareBar />
      <ChatWidget />
      <CookieConsent />
      {/* ChatBot (Cherry) moved to Tixaru -- not mounted on the public
          website. Component itself untouched, just not mounted. */}
    </>
  );
}

// Root App — Router Wrapper
function App() {
  return (
    <CartProvider>
      <CompareProvider>
        <Router>
          <CanonicalTag />
          <RouteSeo />
          <Layout />
        </Router>
      </CompareProvider>
    </CartProvider>
  );
}

export default App;